// @flow
import styled, { css } from 'styled-components';
import { getFont } from '@finect/tabular-utils/getFont';
import { hoverMediaQuery, maxMediaQueries } from '@finect/tabular-utils/mediaQueries';

// Ancho de la columna de horas. Cabe "hace 59 min" a getFont(-1) sin partirse.
// Tiene que caber la etiqueta relativa más larga sin partirse: "hace 3 semanas" y "hace 11 meses"
// miden unos 88 px a 13 px, así que 76 se quedaba corto y la columna es nowrap.
export const TIME_COLUMN_WIDTH = 96;

// Miniatura en 16:10. A 260 px sobre la columna de 1142 px de MainContent la imagen pesa lo
// suficiente para funcionar como gancho sin comerse la medida de línea del texto.
export const THUMB_WIDTH = 260;
const THUMB_HEIGHT = 163;

const plusStyles = css`
  background-color: ${({ theme }) => theme.plus.grey};

  ${hoverMediaQuery`
    &:hover {
      background-color: ${({ theme }) => theme.plus.greyD};
    }
  `}

  &:active {
    background-color: ${({ theme }) => theme.plus.greyD};
  }
`;

/* Sin raya entre filas: las separa el aire del padding, y la única raya del feed es la de la
   cabecera de día, que así se lee como lo que es —un cambio de día— y no como un divisor más. */
export const FeedRow: any = styled.article`
  position: relative;
  display: grid;
  grid-template-columns: ${TIME_COLUMN_WIDTH}px minmax(0, 1fr) ${THUMB_WIDTH}px;
  gap: 0 24px;
  padding: 18px 12px;
  border-radius: 6px;
  margin: 0 -12px;
  transition: background-color 0.12s ease;

  /* El hover, SOLO donde hay un puntero de verdad. En una pantalla táctil el navegador lo dispara
     al posar el dedo —también cuando ese dedo solo va a desplazar— y lo deja puesto al levantarlo,
     así que las filas se encendían solas durante el scroll y se quedaban encendidas al volver
     atrás. No hay nada que un dedo pueda "sobrevolar". */
  ${hoverMediaQuery`
    &:hover {
      background-color: ${({ theme }) => theme.colors.uiLL};
    }
  `}

  /* Lo que sí significa algo al tocar: estoy pulsando ESTO. El navegador ya distingue pulsar de
     desplazar —aplica :active con un pequeño retardo y lo retira en cuanto el gesto se convierte
     en scroll—, así que sale gratis lo que un detector de pulsación larga haría con escuchas y
     temporizadores. Hace falta pintarlo porque el reset del design system quita el destello
     nativo del toque (-webkit-tap-highlight-color: transparent). */
  &:active {
    background-color: ${({ theme }) => theme.colors.uiLL};
  }

  ${({ plus }) => plus && plusStyles}

  ${({ withImage }) =>
    !withImage &&
    css`
      grid-template-columns: ${TIME_COLUMN_WIDTH}px minmax(0, 1fr);
    `}

  ${maxMediaQueries.phablet`
    grid-template-columns: 1fr;
    gap: 12px;
    padding: 18px 0;
    margin: 0;
  `}
`;

export const TimeCell: any = styled.div`
  ${getFont(-1, 'compact')}
  color: ${({ theme }) => theme.colors.fontLL};
  font-variant-numeric: tabular-nums;
  text-align: right;
  white-space: nowrap;
  padding-top: 3px;

  ${({ fresh, theme }) =>
    fresh &&
    css`
      color: ${theme.colors.primary};
      font-weight: 600;
    `}

  ${maxMediaQueries.phablet`
    text-align: left;
    padding-top: 0;
  `}
`;

export const FeedRowContent: any = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const Kicker: any = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 5px;
  ${getFont(-2, 'compact')}
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary};

  a {
    position: relative;
    z-index: 1;
    color: currentColor;
  }

  ${hoverMediaQuery`
    a:hover {
      text-decoration: underline;
    }
  `}
`;

export const KickerProfessional: any = styled.span`
  color: ${({ theme }) => theme.colors.accent3DD};
`;

export const KickerSeparator: any = styled.span`
  color: ${({ theme }) => theme.colors.ui};
  font-weight: 400;
`;

export const PlusChip: any = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 7px;
  border-radius: 4px;
  background: ${({ theme }) => theme.plus.background};
  color: #fff;
  letter-spacing: 0.06em;
  text-transform: none;
`;

export const PlusChipButton: any = styled.button`
  position: relative;
  z-index: 1;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  font: inherit;
  letter-spacing: inherit;
`;

/* Un paso menos de escala en escritorio: 22 px en vez de 26, que es lo que ya llevaba por debajo de
   miniDesktop, así que esa excepción sobra. En móvil se queda en 18. */
export const FeedHeadline: any = styled.h3`
  ${getFont(2, 'compact')}
  font-weight: 700;
  letter-spacing: -0.014em;
  color: ${({ theme }) => theme.colors.font};
  max-width: 40ch;
  margin: 0 0 6px;
  text-wrap: balance;

  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  ${maxMediaQueries.phablet`
    max-width: none;
    ${getFont(1, 'xcompact')}
  `}
`;

export const HeadlineLink: any = styled.a`
  color: currentColor;

  /* El pseudo-elemento cubre la fila entera, así que se puede pulsar en cualquier punto sin
     anidar enlaces. Todo lo que deba seguir siendo pulsable por su cuenta (tema, autor, enlace
     externo) se levanta con position relative y z-index 1. */
  &::after {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    content: '';
  }

  ${hoverMediaQuery`
    ${FeedRow}:hover & {
      color: ${({ theme }) => theme.colors.primaryD};
      text-decoration: underline;
    }
  `}
`;

export const Dek: any = styled.p`
  ${getFont(-1, 'compact')}
  color: ${({ theme }) => theme.colors.fontL};
  max-width: 62ch;
  margin: 0 0 10px;

  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  ${maxMediaQueries.phablet`
    max-width: none;
  `}
`;

export const MetaRow: any = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 6px;
  margin-top: auto;
  ${getFont(-2, 'compact')}
  color: ${({ theme }) => theme.colors.fontLL};

  ${hoverMediaQuery`
    a:hover {
      color: ${({ theme }) => theme.linkColor};
    }
  `}

  a {
    color: currentColor;
  }
`;

export const MetaAvatar: any = styled.span`
  display: inline-flex;
  flex-shrink: 0;

  img {
    display: block;
  }
`;

/**
 * Nombre del autor en la firma de una fila del feed.
 *
 * Va dentro de un `EntityCard` —el mismo componente que pinta la ficha flotante en la cabecera del
 * artículo— así que al pasar el ratón aparece el perfil. Es un `<a>` y no un `<span>` para que el
 * cursor sea de enlace y se pueda ir al perfil; cuando el autor no tiene perfil público se pinta
 * con `as="span"` y entonces el selector de abajo devuelve el cursor normal.
 */
export const MetaName: any = styled.a`
  color: ${({ theme }) => theme.colors.font};
  font-weight: 600;
  cursor: pointer;

  &:visited {
    color: ${({ theme }) => theme.colors.font};
  }

  ${hoverMediaQuery`
    &:hover {
      color: ${({ theme }) => theme.colors.fontL};
      text-decoration: underline;
    }

    /* Sin perfil público se pinta como <span>, y ahí no hay nada que pulsar. */
    &:not([href]):hover {
      color: ${({ theme }) => theme.colors.font};
      text-decoration: none;
    }
  `}

  &:not([href]) {
    cursor: default;
  }
`;

export const MetaSeparator: any = styled.span`
  color: ${({ theme }) => theme.colors.primaryL};
`;

export const ExternalSource: any = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  svg {
    flex-shrink: 0;
  }
`;

export const ThumbCell: any = styled.div`
  position: relative;
  align-self: start;
  overflow: hidden;
  width: ${THUMB_WIDTH}px;
  height: ${THUMB_HEIGHT}px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.colors.uiL};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  ${maxMediaQueries.phablet`
    order: -1;
    width: 100%;
    height: auto;
    aspect-ratio: 16 / 10;
  `}
`;

export const TypeBadge: any = styled.span`
  position: absolute;
  bottom: 8px;
  left: 8px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 7px;
  border-radius: 4px;
  background-color: rgb(34 34 38 / 0.82);
  ${getFont(-2, 'compact')}
  font-weight: 600;
  color: #fff;
  font-variant-numeric: tabular-nums;

  /* Los iconos de tabular vienen con fill none y solo trazo; a 10 px un triangulo hueco no se
     lee, asi que lo rellenamos con el color del texto del badge. */
  svg polygon,
  svg path {
    fill: currentColor;
  }
`;
